import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, 
  Upload, 
  Download, 
  Eye, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  X,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Document {
  id: string;
  name: string;
  type: string;
  size: string;
  status: 'verified' | 'pending' | 'rejected' | 'required';
  uploadedAt?: string;
  description: string;
}

const initialDocuments: Document[] = [
  {
    id: 'DOC-001',
    name: 'Business Registration Certificate',
    type: 'PDF',
    size: '2.4 MB',
    status: 'verified',
    uploadedAt: '2024-01-15',
    description: 'CAC Business Registration',
  },
  {
    id: 'DOC-002',
    name: 'Government ID',
    type: 'JPG',
    size: '1.8 MB',
    status: 'verified',
    uploadedAt: '2024-01-15',
    description: 'International Passport',
  },
  {
    id: 'DOC-003',
    name: 'Proof of Address',
    type: 'PDF',
    size: '3.1 MB',
    status: 'pending',
    uploadedAt: '2024-01-20',
    description: 'Utility Bill',
  },
  {
    id: 'DOC-004',
    name: 'Tax Clearance Certificate',
    type: 'PDF',
    size: '1.2 MB',
    status: 'required',
    description: 'Annual Tax Clearance',
  },
];

export default function VendorDocuments() {
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewingDoc, setViewingDoc] = useState<Document | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleUpload = (docId: string) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          setDocuments(docs => docs.map(doc => 
            doc.id === docId 
              ? { ...doc, status: 'pending', uploadedAt: new Date().toISOString().split('T')[0] }
              : doc
          ));
          toast({
            title: 'Document Uploaded',
            description: 'Your document has been uploaded and is pending verification.',
          });
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleNewUpload = () => {
    toast({
      title: 'Upload Document',
      description: 'Please select a document to upload.',
    });
  };

  const handleView = (doc: Document) => {
    if (doc.status === 'required') {
      toast({
        title: 'Document Required',
        description: 'Please upload this document first.',
        variant: 'destructive',
      });
      return;
    }
    setViewingDoc(doc);
  };

  const handleDownload = (doc: Document) => {
    if (doc.status === 'required') {
      toast({
        title: 'Document Not Available',
        description: 'This document has not been uploaded yet.',
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: 'Download Started',
      description: `Downloading ${doc.name}...`,
    });
    // Simulate download
    setTimeout(() => {
      toast({
        title: 'Download Complete',
        description: `${doc.name} has been downloaded.`,
      });
    }, 1500);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    toast({
      title: 'Files Detected',
      description: 'Processing uploaded files...',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
        <p className="text-muted-foreground">Manage your verification documents</p>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Document Verification Progress</span>
            <span className="text-sm text-muted-foreground">
              {documents.filter(d => d.status === 'verified').length} of {documents.length} verified
            </span>
          </div>
          <Progress 
            value={(documents.filter(d => d.status === 'verified').length / documents.length) * 100} 
            className="h-2" 
          />
        </CardContent>
      </Card>

      {/* Documents Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {documents.map((doc) => (
          <Card key={doc.id}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={cn(
                  "h-12 w-12 rounded-lg flex items-center justify-center shrink-0",
                  doc.status === 'verified' ? 'bg-emerald-500/10' :
                  doc.status === 'pending' ? 'bg-amber-500/10' :
                  doc.status === 'rejected' ? 'bg-red-500/10' :
                  'bg-muted'
                )}>
                  <FileText className={cn(
                    "h-6 w-6",
                    doc.status === 'verified' ? 'text-emerald-500' :
                    doc.status === 'pending' ? 'text-amber-500' :
                    doc.status === 'rejected' ? 'text-red-500' :
                    'text-muted-foreground'
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{doc.name}</p>
                    {doc.status === 'verified' && (
                      <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {doc.status === 'pending' && (
                      <Badge className="bg-amber-500/20 text-amber-500 text-[10px]">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                    {doc.status === 'rejected' && (
                      <Badge className="bg-red-500/20 text-red-500 text-[10px]">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Rejected
                      </Badge>
                    )}
                    {doc.status === 'required' && (
                      <Badge className="bg-muted text-muted-foreground text-[10px]">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{doc.description}</p>
                  {doc.uploadedAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Uploaded: {doc.uploadedAt}
                    </p>
                  )}
                </div>
              </div>

              {/* Upload Progress */}
              {isUploading && doc.status === 'required' && (
                <div className="mt-4">
                  <Progress value={uploadProgress} className="h-1" />
                  <p className="text-xs text-muted-foreground mt-1 text-center">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                {doc.status !== 'required' ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleView(doc)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => handleUpload(doc.id)}
                    disabled={isUploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upload New */}
      <Card>
        <CardHeader>
          <CardTitle>Upload New Document</CardTitle>
          <CardDescription>Add additional verification documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              isDragging ? 'border-emerald-500 bg-emerald-500/5' : 'border-muted hover:bg-muted/50'
            )}
            onClick={handleNewUpload}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="font-medium">Click to upload or drag and drop</p>
            <p className="text-sm text-muted-foreground mt-1">
              PDF, JPG, or PNG up to 10MB
            </p>
          </div>
        </CardContent>
      </Card>

      {/* View Document Dialog */}
      <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.name}</DialogTitle>
            <DialogDescription>{viewingDoc?.description}</DialogDescription>
          </DialogHeader>
          <div className="p-8 bg-muted rounded-lg text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Document Preview</p>
            <p className="text-sm text-muted-foreground mt-2">
              {viewingDoc?.type} • {viewingDoc?.size}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => viewingDoc && handleDownload(viewingDoc)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button 
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              onClick={() => setViewingDoc(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
